import * as THREE from 'three';
import { FARM_VEHICLE, FARM_VEHICLE_MODEL } from '../config/farmVehicle';
import { boxCenter, boxSize, type SolidSpec } from '../world/Level';

/** Parked machinery faces +X: trailer, drawbar, then the tractor's bonnet. */
export function buildFarmVehicle(spec: SolidSpec): THREE.Group {
  const group = new THREE.Group();
  group.name = spec.name;
  const center = boxCenter(spec.box);
  const actualSize = boxSize(spec.box);
  const size = spec.shape === 'trailer' || spec.shape === 'tractor'
    ? FARM_VEHICLE_MODEL[spec.shape] : actualSize;
  group.scale.set(actualSize.x / size.x, actualSize.y / size.y, actualSize.z / size.z);
  group.position.set(center.x, spec.box.minY, center.z);
  const materials = Object.fromEntries(Object.entries(FARM_VEHICLE).map(([key, color]) =>
    [key, new THREE.MeshLambertMaterial({ color })],
  ));
  const cube = new THREE.BoxGeometry(1, 1, 1);
  const box = (name: string, x: number, y: number, z: number, w: number, h: number, d: number, paint: keyof typeof FARM_VEHICLE) => {
    const mesh = new THREE.Mesh(cube, materials[paint]);
    mesh.name = name;
    mesh.position.set(x, y, z);
    mesh.scale.set(w, h, d);
    group.add(mesh);
    return mesh;
  };
  const wheel = (x: number, radius: number, width: number, z: number) => {
    const tyre = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, width, 16), materials.tyre);
    tyre.rotation.x = Math.PI / 2;
    tyre.position.set(x, radius, z);
    group.add(tyre);
    const rim = new THREE.Mesh(new THREE.CylinderGeometry(radius * 0.52, radius * 0.52, width + 0.02, 12), materials.rim);
    rim.rotation.x = Math.PI / 2;
    rim.position.copy(tyre.position);
    group.add(rim);
    // Raised rubber blocks make the large rear wheels read as tractor tyres.
    for (let i = 0; i < 16; i++) {
      const angle = i * Math.PI / 8;
      const tread = box('rengaskuvio', x + Math.sin(angle) * (radius - 0.025),
        radius + Math.cos(angle) * (radius - 0.025), z, radius * 0.24, 0.05, width * 0.96, 'tyre');
      tread.rotation.z = -angle;
    }
  };
  if (spec.shape === 'drawbar') {
    box('vetoaisa', 0, size.y - 0.14, 0, size.x, 0.28, size.z, 'frame');
    return group;
  }
  const length = size.x;
  const width = size.z;
  if (spec.shape === 'trailer') {
    box('runko', 0, 0.82, 0, length, 0.34, width * 0.82, 'frame');
    box('lava', 0, 1.14, 0, length, 0.3, width, 'trailer');
    for (const side of [-1, 1]) {
      box('laita', 0, 1.77, side * (width / 2 - 0.08), length, 1.05, 0.16, 'trailer');
      for (let i = 0; i < 8; i++) box('laidan-tuki', -length / 2 + 0.18 + i * (length - 0.36) / 7,
        1.77, side * (width / 2 - 0.03), 0.12, 1.08, 0.06, 'frame');
      for (const axle of [-0.31, -0.15, 0.28]) wheel(axle * length, 0.72, 0.42, side * (width / 2 - 0.24));
    }
    for (const end of [-1, 1]) box('paatylaita', end * (length / 2 - 0.08), 1.77, 0, 0.16, 1.05, width, 'trailer');
    // A loaded hay wagon explains the tall, continuous collision silhouette.
    for (let i = 0; i < 7; i++) {
      const bale = box('heinapaali', -length / 2 + (i + 0.5) * length / 7, 2.71, 0,
        length / 7 - 0.08, 1.16, width - 0.24, 'hay');
      for (const offset of [-0.3, 0.3]) box('paalin-naru', bale.position.x + offset * length / 7,
        2.72, 0, 0.035, 1.17, width - 0.22, 'rim');
    }
  } else {
    const rear = -length * 0.28;
    const front = length * 0.3;
    box('alusta', 0, 1.12, 0, length * 0.95, 0.45, width * 0.5, 'frame');
    box('konepelti', length * 0.19, 1.85, 0, length * 0.5, 1.02, width * 0.58, 'tractor');
    box('jaahdytin', length * 0.446, 1.82, 0, 0.07, 0.77, width * 0.48, 'frame');
    for (let i = 0; i < 7; i++) box('maskin-ripa', length * 0.453, 1.52 + i * 0.09, 0, 0.05, 0.024, width * 0.45, 'rim');
    box('ohjaamo', rear + 0.2, 2.52, 0, length * 0.31, 1.54, width * 0.62, 'glass');
    for (const x of [rear + 0.2 - length * 0.155, rear + 0.2 + length * 0.155]) {
      for (const side of [-1, 1]) box('ohjaamon-pilari', x, 2.52, side * width * 0.305, 0.09, 1.65, 0.09, 'rim');
    }
    box('katto', rear + 0.2, size.y - 0.11, 0, length * 0.35, 0.22, width * 0.72, 'rim');
    box('pakoputki', length * 0.08, 2.79, -width * 0.24, 0.14, 1.25, 0.14, 'frame');
    for (const side of [-1, 1]) {
      wheel(rear, 1.18, 0.63, side * (width / 2 - 0.33));
      wheel(front, 0.78, 0.45, side * (width / 2 - 0.29));
      box('lokasuoja', rear, 2.44, side * (width / 2 - 0.36), 2.5, 0.12, 0.72, 'tractor');
      box('ajovalo', length * 0.45, 2.04, side * width * 0.19, 0.08, 0.25, 0.34, 'lamp');
      box('askelma', -length * 0.07, 0.66, side * width * 0.32, 0.66, 0.14, 0.52, 'frame');
    }
    box('vetokoukku', -length * 0.49, 1.17, 0, length * 0.1, 0.28, 0.44, 'frame');
  }
  return group;
}
